import clsx from 'clsx';
import { forwardRef, type HTMLAttributes, type ImgHTMLAttributes, useEffect, useState } from 'react';

/**
 * A simple Avatar component matching the Radix API but built with plain JSX.
 * Headless UI doesn't provide an Avatar primitive, so this is a custom implementation.
 */

const Avatar = forwardRef<HTMLSpanElement, HTMLAttributes<HTMLSpanElement>>(({ className, ...props }, ref) => {
    return (
        <span
            className={ clsx(
                'relative flex h-10 w-10 shrink-0 overflow-hidden rounded-full',
                className
            ) }
            ref={ ref }
            { ...props }
        />
    );
});

Avatar.displayName = 'Avatar';

interface AvatarImageProps extends ImgHTMLAttributes<HTMLImageElement> {
    onLoadingStatusChange?: (status: 'loaded' | 'error') => void;
}

const AvatarImage = forwardRef<HTMLImageElement, AvatarImageProps>(
    ({ className, src, alt, onLoadingStatusChange, ...props }, ref) => {
        const [
            status,
            setStatus,
        ] = useState<'loading' | 'loaded' | 'error'>('loading');

        useEffect(() => {
            if (!src) {
                setStatus('error');
                return;
            }

            const img = new Image();

            img.src = src;
            img.onload = () => {
                setStatus('loaded');
                onLoadingStatusChange?.('loaded');
            };
            img.onerror = () => {
                setStatus('error');
                onLoadingStatusChange?.('error');
            };
        }, [
            src,
            onLoadingStatusChange,
        ]);

        if (status !== 'loaded') {
            return null;
        }

        return (
            <img
                alt={ alt }
                className={ clsx('aspect-square h-full w-full object-cover', className) }
                ref={ ref }
                src={ src }
                { ...props }
            />
        );
    }
);

AvatarImage.displayName = 'AvatarImage';

interface AvatarFallbackProps extends HTMLAttributes<HTMLSpanElement> {
    delayMs?: number;
}

const AvatarFallback = forwardRef<HTMLSpanElement, AvatarFallbackProps>(
    ({ className, delayMs = 0, children, ...props }, ref) => {
        const [
            canRender,
            setCanRender,
        ] = useState(delayMs === 0);

        useEffect(() => {
            if (delayMs > 0) {
                const timer = setTimeout(() => {
                    return setCanRender(true);
                }, delayMs);

                return () => {
                    return clearTimeout(timer);
                };
            }
        }, [
            delayMs,
        ]);

        if (!canRender) {
            return null;
        }

        return (
            <span
                className={ clsx(
                    'flex h-full w-full items-center justify-center rounded-full bg-muted',
                    className
                ) }
                ref={ ref }
                { ...props }
            >
                {children}
            </span>
        );
    }
);

AvatarFallback.displayName = 'AvatarFallback';

export { Avatar, AvatarFallback, AvatarImage };
